import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, ArrowRight, Heart, Sparkles, Globe2 } from 'lucide-react';
import { safeHtml } from '../lib/sanitize';

/**
 * Homepage Expert Card — promotes ViratAstro's Dubai-based expert Priya Khurana.
 * Positioned after Vivek जी's About section to complement his Vedic offering
 * with English-market Tarot + Relationship Coaching.
 */
export default function ExpertSpotlight() {
  return (
    <section className="py-14 sm:py-20 bg-bg-secondary relative overflow-hidden" data-testid="expert-spotlight">
      <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_70%_30%,rgba(255,215,0,0.10),transparent_60%),radial-gradient(circle_at_30%_80%,rgba(236,72,153,0.08),transparent_60%)]" />
      <div className="container mx-auto px-4 max-w-6xl relative">
        <div className="text-center mb-10">
          <span className="text-xs tracking-[0.3em] text-gold/70 uppercase">Featured Tarot Expert at ViratAstro</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mt-2 mb-3">
            Dubai-Based Tarot &amp; Relationship Coach
          </h2>
          <p className="text-text-muted max-w-2xl mx-auto">
            Beyond Acharya Vivek Mudgal's Vedic offerings, ViratAstro brings you internationally trained tarot &amp; coaching guidance — curated specifically for clarity in love, relationships and personal growth.
          </p>
        </div>

        <Link
          to="/experts/priya-khurana"
          className="group block rounded-3xl bg-gradient-to-br from-bg-card to-bg-primary border border-gold/30 hover:border-gold/60 overflow-hidden transition-all hover:shadow-[0_0_40px_rgba(255,215,0,0.15)]"
          data-testid="priya-expert-card"
        >
          <div className="grid md:grid-cols-5">
            {/* Image */}
            <div className="md:col-span-2 relative aspect-[4/3] md:aspect-auto md:min-h-[380px] overflow-hidden">
              <img
                src="/experts/priya-khurana-card.png"
                alt="Priya Khurana — Featured Tarot Expert at ViratAstro · Dubai"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-bg-primary/80 via-transparent md:bg-gradient-to-r md:from-transparent md:to-bg-primary/40" />
              <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-bg-primary/80 backdrop-blur-sm border border-gold/30 text-xs">
                <MapPin className="w-3 h-3 text-gold" /> <span className="text-text-primary">Dubai, UAE</span>
              </div>
            </div>

            {/* Content */}
            <div className="md:col-span-3 p-7 sm:p-10 flex flex-col justify-center">
              <p className="text-xs tracking-[0.25em] text-gold uppercase mb-2">Priya Khurana</p>
              <h3 className="text-2xl sm:text-3xl font-heading font-bold mb-3 leading-tight">
                Tarot Teacher · Relationship Coach · Self-Awareness Mentor
              </h3>
              <p className="text-sm text-text-muted leading-relaxed mb-5">
                <b className="text-text-primary">6+ years</b> of professional practice with advanced training in <b className="text-text-primary">Canada</b> &amp; <b className="text-text-primary">Singapore</b>. Confidential online consultations across UAE, India, Canada, Singapore, UK and the US.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
                {[
                  { i: Heart, t: 'Love &amp; Relationships' },
                  { i: Sparkles, t: 'Marriage Compatibility' },
                  { i: Globe2, t: 'Online Worldwide' },
                ].map(({ i: Icon, t }) => (
                  <div key={t} className="flex items-center gap-1.5 text-xs text-text-muted">
                    <Icon className="w-3.5 h-3.5 text-gold" /> <span dangerouslySetInnerHTML={safeHtml(t)} />
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-bg-primary font-semibold text-sm group-hover:bg-yellow-400 transition">
                  View Profile <ArrowRight className="w-4 h-4" />
                </span>
                <a
                  href="https://wa.me/971508238898?text=Namaste%20Priya%2C%20I%20would%20like%20to%20book%20a%20Tarot%20session."
                  target="_blank" rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-600 text-white font-semibold text-sm hover:bg-green-700 transition"
                  data-testid="expert-card-whatsapp"
                >
                  <Phone className="w-4 h-4" /> +971 50 823 8898
                </a>
              </div>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
}
